import { useState, type SyntheticEvent } from 'react'
import type { TFunction } from 'i18next'
import { OTHER_SALES_POLE_ID, type SalesPole } from './poles'
import { moveFocus } from './moveFocus'
import { polePopoverOverrides } from './styled-components'

export interface PoleOptionItem {
  id: string
  name: string
  countLabel: string
  isSelected: boolean
  hasDividerBefore: boolean
  onChoose: () => void
}

interface UsePolePickerParams {
  t: TFunction
  poles: SalesPole[]
  pole: SalesPole | null
  countOf: (pole: SalesPole) => number
  onSelect: (id: string) => void
}

const usePolePicker = ({ t, poles, pole, countOf, onSelect }: UsePolePickerParams) => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuWidth, setMenuWidth] = useState(0)
  const nameOf = (id: string) => t(`additionalSales:pulse.poles.${id}`)

  const toggle = (event: SyntheticEvent) => {
    setMenuWidth((event.currentTarget as HTMLElement).offsetWidth)
    setIsOpen(open => !open)
  }

  const close = () => setIsOpen(false)

  const options: PoleOptionItem[] = poles.map(candidate => ({
    id: candidate.id,
    name: nameOf(candidate.id),
    countLabel: t('additionalSales:pulse.sales_count', { count: countOf(candidate) }),
    isSelected: candidate.id === pole?.id,
    hasDividerBefore: candidate.id === OTHER_SALES_POLE_ID,
    onChoose: () => {
      setIsOpen(false)
      onSelect(candidate.id)
    },
  }))

  return {
    isOpen,
    menuWidth,
    toggle,
    close,
    options,
    popoverOverrides: polePopoverOverrides,
    onMenuKeyDown: moveFocus('[role="option"]'),
    myPoleLabel: t('additionalSales:pulse.my_pole'),
    triggerValue: pole ? nameOf(pole.id) : t('additionalSales:pulse.choose_pole'),
  }
}

export default usePolePicker
